// Шаблоны XYZ-тайлов бэкенда (плейсхолдеры {z}/{x}/{y} подставляет MapLibre).

/** Shared-слой (сырое поле): sst/salinity/currents/... по дате и глубине.
 *
 *  `version` — метка последней генерации тайлов (availability.tiles_version).
 *  Тайлы отдаются с max-age=86400 при неизменном URL, поэтому без версии после
 *  «Загрузить данные» браузер сутки показывал бы старую картинку (сервер 304
 *  не отдаёт — ревалидация каждый раз означала бы перекачку всех тайлов).
 */
export function sharedTileUrl(
  date: string,
  layer: string,
  depth: number,
  version?: string | null,
  palette?: string | null,
): string {
  const base = `/api/v1/tiles/raster/shared/${date}/${layer}_${depth}/{z}/{x}/{y}.png`
  const q: string[] = []
  if (version) q.push(`v=${encodeURIComponent(version)}`)
  // Палитра бренда — ПАРАМЕТРОМ, а не сегментом пути: базовый адрес обязан
  // остаться прежним, под ним лежат гигабайты уже отрисованных тайлов.
  // Пусто у домена без своей гаммы — и тогда URL в точности прежний.
  if (palette) q.push(`p=${encodeURIComponent(palette)}`)
  return q.length ? `${base}?${q.join('&')}` : base
}

/** Probability (HSI) для вида по дате и глубине.
 *
 *  `version` — метка последней генерации тайлов (raster status `tiles_version`).
 *  Без неё пересчитанное поле не доезжает до экрана: тайлы отдаются с
 *  max-age=86400 при неизменном URL, и браузер сутки показывает свою копию.
 *  2026-08-04 это и случилось — 4964 свежих PNG на диске и прежняя картинка в
 *  окне даже после перезагрузки. Кнопка «Загрузить» защищена cache-buster'ом,
 *  автопоказу при входе в модель нужна версия.
 */
export function probabilityTileUrl(
  speciesId: number,
  date: string,
  depth: number,
  version?: string | null,
): string {
  const base = `/api/v1/tiles/raster/${speciesId}/${date}/probability_${depth}/{z}/{x}/{y}.png`
  return version ? `${base}?v=${encodeURIComponent(version)}` : base
}

/** Версия отрисовки тайлов батиметрии. Тайл кэшируется браузером на неделю
 *  (`max-age=604800`), поэтому смена рисунка — новая версия в адресе: 2 —
 *  изобаты по лестнице зума, 3 — своя лестница тайла, на ступень грубее (28.10.2026); до 2 в тайл шли все уровни. */
export const BATHYMETRY_TILE_VERSION = 3

/** Батиметрия — статический слой без даты. */
export function bathymetryTileUrl(): string {
  return `/api/v1/tiles/raster/bathymetry/{z}/{x}/{y}.png?v=${BATHYMETRY_TILE_VERSION}`
}

/** Облачность (ERA5 tcc) по дате — ленивый рендер из таблицы clouds. */
export function cloudTileUrl(date: string): string {
  return `/api/v1/tiles/raster/clouds/${date}/{z}/{x}/{y}.png`
}

/** Апвеллинг (индекс отрыва планктона) по дате — из артефакта расчёта.
 *
 *  Поле `total_index` на сетке GEBCO (1/240°), то же, что на телеграм-карте;
 *  geo9-агрегат (максимум по ячейке ~9 км) остался входом модели HSI.
 *
 *  `version` — метка пересчёта (availability.tiles_version): тайлы ленивые, но
 *  отдаются с max-age=86400, а пересчёт индекса URL не меняет. См. sharedTileUrl.
 */
export function upwellingTileUrl(date: string, version?: string | null): string {
  const base = `/api/v1/tiles/raster/upwelling/${date}/{z}/{x}/{y}.png`
  return version ? `${base}?v=${encodeURIComponent(version)}` : base
}

/** Механизм апвеллинга по дате — из артефакта расчёта (`src/upwelling/store.py`).
 *
 *  Механизм и горизонт входят в ПУТЬ, а не в параметр запроса: под ними разные
 *  поля, и общий адрес означал бы общий кэш браузера — переключив горизонт,
 *  человек смотрел бы на прежнюю картинку.
 *
 *  `version` — метка пересчёта: тайлы ленивые, но отдаются с max-age=86400, а
 *  пересчёт поля адреса не меняет (см. `sharedTileUrl`).
 */
export function upwMechTileUrl(
  mechanism: string,
  date: string,
  depth?: number | null,
  version?: string | null,
): string {
  const base = `/api/v1/tiles/raster/upw/${mechanism}/${date}/{z}/{x}/{y}.png`
  const q: string[] = []
  if (depth != null) q.push(`depth=${encodeURIComponent(String(depth))}`)
  if (version) q.push(`v=${encodeURIComponent(version)}`)
  return q.length ? `${base}?${q.join('&')}` : base
}

/** Морской туман (нет/дымка/туман) по дате — из полей ERA5 air_surface. */
export function fogTileUrl(date: string): string {
  return `/api/v1/tiles/raster/fog/${date}/{z}/{x}/{y}.png`
}

/** Карта нейросети для вида по дате — из артефакта `src/fishnet/field_store.py`.
 *
 *  ВИД входит в ПУТЬ, а не в параметр запроса: под разными видами разные поля, и
 *  общий адрес означал бы общий кэш браузера — переключив вид, человек смотрел
 *  бы на прежнюю картинку.
 *
 *  Глубины у слоя нет вовсе: сеть отвечает о столбе воды целиком, и горизонт
 *  общего глубиномера витрины её ответа не меняет.
 *
 *  `version` — метка расчёта (`availability.tiles_version`): тайлы ленивые, но
 *  отдаются с max-age=86400, а пересчёт поля адреса не меняет (см. `sharedTileUrl`).
 */
export function nnTileUrl(
  speciesId: number,
  date: string,
  version?: string | null,
): string {
  const base = `/api/v1/tiles/raster/nn/${speciesId}/${date}/{z}/{x}/{y}.png`
  return version ? `${base}?v=${encodeURIComponent(version)}` : base
}

/** URL тайла оверлея по его id (clouds/sea_fog/ice) на дату. */
export function overlayTileUrl(id: string, date: string): string | null {
  if (id === 'clouds') return cloudTileUrl(date)
  if (id === 'sea_fog') return fogTileUrl(date)
  if (id === 'ice') return sharedTileUrl(date, 'ice', 0)
  return null
}
